import useSWR from "swr";
import { useAuthToken } from "./useAuthToken";
import { getStoreFrontDesignImagesWithAuthRetry } from "services/designImagesAuth";
import { customerGetDesignImages } from "services/storeFront";
import { useAppSelector } from "store";
import { isExpiredAuthTokenError } from "utils/errorUtils";

type DesignImagesData = {
  largeImageUrl: string;
  smallImageUrl: string;
  pinsImageUrl?: string;
};

type StorefrontDesignImagesResponse = {
  proposalLarge?: { url: string };
  proposalSmall?: { url: string };
  pins?: { url: string };
};

const mapDesignImagesResponse = (
  response: StorefrontDesignImagesResponse
): DesignImagesData => ({
  largeImageUrl: response.proposalLarge?.url ?? "",
  smallImageUrl: response.proposalSmall?.url ?? "",
  pinsImageUrl: response.pins?.url,
});

/** First non-empty URL; `??` misses empty strings from the design images API. */
const pickDesignImageUrl = (
  ...candidates: (string | undefined)[]
): string | undefined => {
  for (const url of candidates) {
    if (url?.trim()) return url;
  }
  return undefined;
};

const useStorefrontDesignImages = (
  designId?: string,
  designVersion?: number
) => {
  const ihdToken = useAuthToken("IHD");
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const authToken = cognitoToken ?? ihdToken?.token;

  return useSWR(
    [
      "storefrontDesignImages",
      authToken,
      proposalToken,
      designId,
      designVersion,
    ],
    ([, token, proposalToken, designId, designVersion]) => {
      if (!designId || designVersion == null) return;

      if (proposalToken) {
        return customerGetDesignImages(
          proposalToken,
          designId,
          designVersion
        ).then(mapDesignImagesResponse);
      }

      if (!token) return;
      return getStoreFrontDesignImagesWithAuthRetry(
        token,
        designId,
        designVersion
      ).then(mapDesignImagesResponse);
    },
    {
      shouldRetryOnError: (error) => !isExpiredAuthTokenError(error),
    }
  );
};

export { pickDesignImageUrl, useStorefrontDesignImages };
export type { DesignImagesData };
